import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { describeFoetus } from "features/pregnancy/utils/endedFoetuses";

/**
 * Record that a foetus is no longer continuing (KIN-2066, backend KIN-2065).
 *
 * `POST /pregnancyfoetus/{id}/end` with the outcome — not the `/pregnancyfoetus` upsert and not
 * the delete, because nothing is discarded here. The backend keeps the foetus and every
 * observation recorded against it, marks it inactive with its outcome, and lowers the pregnancy's
 * declared foetus count to the remaining live foetuses.
 *
 * Failures are left for the caller to render rather than thrown at a toast. The two 409s are
 * expected answers, not crashes — "already been recorded as not continuing", and "the pregnancy's
 * only remaining foetus. Complete the pregnancy instead." — and they are worth reading beside the
 * foetus that was picked, which a toast over a closed dialog is not.
 */
export const useEndPregnancyFoetus = ({ tenantVal, pregnancyId, onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async ({ foetusId, outcome }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/pregnancyfoetus/${foetusId}/end`,
        { outcome }
      );

      return response.data?.data ?? response.data;
    },
    onSuccess: (endedFoetus) => {
      toast.success(`${describeFoetus(endedFoetus)} recorded as not continuing.`);

      /*
       * The pregnancy, because its foetuses and its declared count both moved; the inbound
       * messages, because the count-differs flag is recomputed on read and this is what settles
       * it; the visits, because each one carries the foetus on its observations and so carries
       * the outcome now displayed.
       */
      queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
      queryClient.invalidateQueries({
        queryKey: ["mncmsInboundMessages", pregnancyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["anteNatalConsultations", pregnancyId],
      });
      queryClient.invalidateQueries({ queryKey: ["foetalObservations"] });
      onSuccess?.();
    },
    onError: (error) => {
      // Never swallowed: the dialog shows the backend's own words, and this keeps the response
      // itself in the console for the cases the message alone does not explain.
      console.error(error);
    },
  });
};
